import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { getHistoryItemColor } from "../../utils";
import { useSocket } from "../../providers/SocketProvider";
import { MessageCircle, ShieldCheck } from "lucide-react";

const GREEN_PROGRESS = "#427f00";
const PURPLE_X = "rgb(192, 23, 180)";
const BLUE_X = "#07BDE5";
const AVATAR_SIZE = 24;
const PLACEHOLDER_AVATAR =
  "https://api.dicebear.com/7.x/identicon/svg?seed=placeholder";

function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function getRandomFloat(min: number, max: number, decimals = 2) {
  const val = Math.random() * (max - min) + min;
  return parseFloat(val.toFixed(decimals));
}

function getDummyTop() {
  const top = [];
  for (let i = 0; i < 32; ++i) {
    top.push({
      username: `d***${getRandomInt(1, 9)}`,
      avatar: `/avatars/av-${getRandomInt(1, 72)}.png`,
      date: "15.07.25",
      bet: getRandomFloat(0.3, 5, 2),
      win: getRandomFloat(300, 9000, 2),
      result: getRandomFloat(0.8, 4, 2),
      roundMax: getRandomFloat(1000, 8000, 2),
      x: getRandomFloat(1000, 4000, 2),
    });
  }
  return top;
}

const Tab = ({
  label,
  active,
  onClick,
  rounded,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  rounded: boolean;
}) => (
  <button
    className={`px-6 rounded-${
      rounded ? "full" : "md"
    } focus:outline-none m-1 w-[100%] transition-all ${
      active ? "bg-[#2c2d30] text-[#bbbfc5]" : "text-[#7b7b7b]"
    }`}
    style={
      rounded
        ? { paddingBlock: "0.15rem" }
        : {
            paddingBlock: "0.4rem",
            borderRadius: "12px",
            paddingInline: "0.5rem",
            fontSize: "12px",
            fontWeight: "500",
            lineHeight: "1.5",
            marginBlock: "0.1rem",
          }
    }
    onClick={onClick}
  >
    {label}
  </button>
);

const BetBoardItem = ({
  username,
  betAmount,
  crashedAt,
  cashout,
  avatar,
  isWinner,
  isYou,
  highlightCashout,
}: any) => {
  return (
    <div
      className={`flex items-center py-1 mb-[2px] rounded-[32px] ${
        isWinner ? "celebrate" : isYou ? "bg-[#232a3a]" : "bg-[#101112]"
      } ${isWinner && highlightCashout ? "bg-green" : ""}`}
      style={{
        color: isWinner ? "#bbbfc5" : isYou ? "#bbbfc5" : "#bbbfc5",
      }}
    >
      <img
        alt="avatar"
        width={AVATAR_SIZE}
        height={AVATAR_SIZE}
        src={avatar}
        onError={(e) => (e.currentTarget.src = PLACEHOLDER_AVATAR)}
        className="rounded-full mr-2 ml-1 border border-[#232325]"
        style={{ objectFit: "cover" }}
      />
      <span className="w-20 truncate text-[12]">{username}</span>
      <span className="w-[4.5rem] text-right font-inter">
        {typeof betAmount === "number" ? betAmount.toFixed(2) : betAmount}
      </span>
      <span
        className="w-16 text-center text-[12px]"
        style={{
          color: crashedAt ? (crashedAt < 2 ? BLUE_X : PURPLE_X) : "#888",
        }}
      >
        {crashedAt ? (
          <span>
            {crashedAt.toFixed(2)}
            <span>x</span>
          </span>
        ) : (
          "-"
        )}
      </span>
      <span className="w-20 text-right font-inter">
        {cashout
          ? typeof cashout === "number"
            ? cashout.toFixed(2)
            : cashout
          : "-"}
      </span>
    </div>
  );
};

type BetBoardProps = {
  onDummyPlayerCountChange?: (count: number) => void;
};

const BetBoard = ({ onDummyPlayerCountChange }: BetBoardProps) => {
  const [tab, setTab] = useState("All Bets");
  const [topTab, setTopTab] = useState("X");
  const [topTabFilter, setTopTabFilter] = useState("Day");

  const { bets, gameData, roundId, currencyConfig, username } = useSocket();

  const [currentRoundBets, setCurrentRoundBets] = useState<any[]>([]);
  const [fakeRoundBets, setFakeRoundBets] = useState<any[]>([]);
  const [dummySpectatorCount, setDummySpectatorCount] = useState(() =>
    getRandomInt(110, 160),
  );
  const [previousRoundBets, setPreviousRoundBets] = useState<any[]>([]);
  const [previousRoundResult, setPreviousRoundResult] = useState<number>(0);
  const [roundHistory, setRoundHistory] = useState<
    { roundId: string; bets: any[]; result: number }[]
  >([]);

  const currentRoundRef = useRef<string>("");
  const prevGameStatusRef = useRef<string>("");
  const currentRoundBetsRef = useRef<any[]>([]);
  const fakeRoundBetsRef = useRef<any[]>([]);
  const fakeRoundIdRef = useRef<string>("");
  const fakeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const dummyCashPlanRef = useRef<
    Record<string, { target: number; done: boolean }>
  >({});
  const dummyCashTargetCountRef = useRef(0);
  const gameStatusRef = useRef<string>("");
  const previousRoundResultRef = useRef<number>(0);
  const roundHistoryRef = useRef<{ roundId: string; bets: any[]; result: number }[]>(
    [],
  );

  useEffect(() => {
    currentRoundBetsRef.current = currentRoundBets;
  }, [currentRoundBets]);

  useEffect(() => {
    previousRoundResultRef.current = previousRoundResult;
  }, [previousRoundResult]);

  useEffect(() => {
    fakeRoundBetsRef.current = fakeRoundBets;
  }, [fakeRoundBets]);

  useEffect(() => {
    // Viewer count represents people watching the game, not the number of
    // bets in the active round. Keep it independent from round bet resets.
    onDummyPlayerCountChange?.(dummySpectatorCount);
  }, [dummySpectatorCount, onDummyPlayerCountChange]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setDummySpectatorCount((previous) => {
        const change = getRandomInt(1, 3) * (Math.random() < 0.5 ? -1 : 1);
        return Math.max(105, Math.min(180, previous + change));
      });
    }, 1800);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(
    () => () => onDummyPlayerCountChange?.(0),
    [onDummyPlayerCountChange],
  );

  useEffect(() => {
    roundHistoryRef.current = roundHistory;
  }, [roundHistory]);

  useEffect(() => {
    gameStatusRef.current = gameData.status || "";
  }, [gameData.status]);

  const mergeRoundBets = useCallback((socketBets: any[], fakeBets: any[]) => {
    const merged: any[] = [];
    const seenIds = new Set<string>();
    const seenIdentity = new Set<string>();

    const addBet = (bet: any) => {
      const betId = bet.betId ? String(bet.betId) : "";
      const name = bet.userName || bet.username || "";
      const stake = Number(bet.stake || 0).toFixed(2);
      const identity = `${name}-${stake}`;

      if (betId && seenIds.has(betId)) return;
      if (seenIdentity.has(identity)) return;

      if (betId) seenIds.add(betId);
      seenIdentity.add(identity);
      merged.push(bet);
    };

    socketBets.forEach(addBet);
    fakeBets.forEach(addBet);
    return merged.sort((a, b) => Number(b.stake || 0) - Number(a.stake || 0));
  }, []);

  const clearFakeTimers = useCallback(() => {
    fakeTimersRef.current.forEach((timer) => clearTimeout(timer));
    fakeTimersRef.current = [];
  }, []);

  const randomStakeForDummy = useCallback(() => {
    const chance = Math.random();
    let min: number;
    let max: number;

    if (chance < 0.6) {
      min = 10;
      max = 50;
    } else if (chance < 0.95) {
      min = 51;
      max = 100;
    } else {
      min = 100;
      max = 1000;
    }

    const step = currencyConfig.stepAmount;
    const stake = Math.round(getRandomInt(min, max) / step) * step;

    return Math.max(
      currencyConfig.minBet,
      Math.min(currencyConfig.maxBet, stake),
    );
  }, [
    currencyConfig.maxBet,
    currencyConfig.minBet,
    currencyConfig.stepAmount,
  ]);

  const createFakeBet = useCallback((roundKey: string) => {
    const firstLetter = String.fromCharCode(97 + getRandomInt(0, 25));
    const lastLetter = String.fromCharCode(97 + getRandomInt(0, 25));

    return {
      betId: `fake-${roundKey}-${Date.now()}-${getRandomInt(1000, 9999)}`,
      userName: `${firstLetter}***${lastLetter}`,
      avatar: `/avatars/av-${getRandomInt(1, 72)}.png`,
      stake: randomStakeForDummy(),
      cashOut: 0,
      oddsRate: 1.0,
      nation: "PLACED",
      status: "PLACED",
      roundId: roundKey,
      createdAt: new Date().toISOString(),
      isDummy: true,
    };
  }, [randomStakeForDummy]);

  const addFakeBet = useCallback(
    (roundKey: string) => {
      if (gameStatusRef.current !== "WAIT") return;

      let candidate = createFakeBet(roundKey);
      let attempts = 0;
      while (attempts < 5) {
        const name = candidate.userName;
        const stake = Number(candidate.stake || 0).toFixed(2);
        const isDuplicate = [...currentRoundBetsRef.current, ...fakeRoundBetsRef.current].some(
          (bet: any) => {
            const betName = bet.userName || bet.username || "";
            const betStake = Number(bet.stake || 0).toFixed(2);
            return betName === name && betStake === stake;
          },
        );

        if (!isDuplicate) break;
        candidate = createFakeBet(roundKey);
        attempts += 1;
      }

      setFakeRoundBets((prev) => [...prev, candidate]);
    },
    [createFakeBet],
  );

  useEffect(() => {
    const roundKey = roundId ? String(roundId) : "";
    if (!roundKey) return;

    if (gameData.status === "WAIT") {
      if (fakeRoundIdRef.current === roundKey) return;

      fakeRoundIdRef.current = roundKey;
      setFakeRoundBets([]);
      clearFakeTimers();
      dummyCashPlanRef.current = {};
      dummyCashTargetCountRef.current = 0;

      const total = getRandomInt(40, 60);
      for (let i = 0; i < total; i += 1) {
        const timer = setTimeout(() => addFakeBet(roundKey), (i + 1) * 150);
        fakeTimersRef.current.push(timer);
      }
    } else {
      clearFakeTimers();
    }

    return () => {
      clearFakeTimers();
    };
  }, [addFakeBet, clearFakeTimers, gameData.status, roundId]);

  useEffect(() => {
    if (gameData.status !== "RUN") return;

    const dummyBets = fakeRoundBetsRef.current;
    if (!dummyBets.length) return;

    const targetCount = Math.max(1, Math.floor(dummyBets.length * 0.7));
    const shuffled = [...dummyBets];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    const plan: Record<string, { target: number; done: boolean }> = {};
    shuffled.slice(0, targetCount).forEach((bet) => {
      plan[bet.betId] = {
        target: 1.05 + Math.pow(Math.random(), 2) * 2.5,
        done: false,
      };
    });

    dummyCashPlanRef.current = plan;
    dummyCashTargetCountRef.current = targetCount;
  }, [gameData.status, roundId]);

  useEffect(() => {
    if (gameData.status !== "RUN") return;
    if (typeof gameData.multiplier !== "number") return;
    if (!dummyCashTargetCountRef.current) return;

    const currentMultiplier = gameData.multiplier;
    const readyBetIds = fakeRoundBetsRef.current
      .filter((bet) => {
        const plan = dummyCashPlanRef.current[bet.betId];
        return plan && !plan.done && plan.target <= currentMultiplier;
      })
      .map((bet) => bet.betId);

    if (!readyBetIds.length) return;

    for (let i = readyBetIds.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [readyBetIds[i], readyBetIds[j]] = [readyBetIds[j], readyBetIds[i]];
    }

    const maxPerTick = Math.max(1, Math.floor(readyBetIds.length * 0.3));
    const cashingIds = new Set(readyBetIds.slice(0, maxPerTick));

    cashingIds.forEach((betId) => {
      const plan = dummyCashPlanRef.current[betId];
      if (plan && !plan.done) {
        plan.done = true;
        dummyCashTargetCountRef.current -= 1;
      }
    });

    setFakeRoundBets((previous) =>
      previous.map((bet) => {
        if (!cashingIds.has(bet.betId)) return bet;

        return {
          ...bet,
          nation: "CASHOUT",
          status: "CASHOUT",
          oddsRate: currentMultiplier,
          cashOut: Number((bet.stake * currentMultiplier).toFixed(2)),
        };
      }),
    );
  }, [gameData.multiplier, gameData.status]);

  useEffect(() => {
    if (gameData.status !== "BLAST") return;

    dummyCashPlanRef.current = {};
    dummyCashTargetCountRef.current = 0;

    const crashValue =
      gameData.multiplier || gameData.crashPoint || 1.0;
    setFakeRoundBets((prev) =>
      prev.map((bet) => ({
        ...bet,
        crashMultiplier: crashValue,
      })),
    );
  }, [gameData.crashPoint, gameData.multiplier, gameData.status]);

  // In BetBoard component, update the useEffect to handle cashout updates better:
  useEffect(() => {
    if (!bets && fakeRoundBetsRef.current.length === 0) return;

    const combinedCurrentBets = mergeRoundBets(
      currentRoundBetsRef.current,
      fakeRoundBetsRef.current,
    );

    // Handle round completion - move current bets to history when game ends
    if (
      gameData.status === "WAIT" &&
      prevGameStatusRef.current === "BLAST" &&
      combinedCurrentBets.length > 0
    ) {
      const roundResult = previousRoundResultRef.current || 1.0;

      // Add current round to history
      setRoundHistory((prev) => {
        const newHistory = [...prev];
        newHistory.unshift({
          roundId: currentRoundRef.current,
          bets: [...combinedCurrentBets],
          result: roundResult,
        });

        // Keep only last 5 rounds
        return newHistory.slice(0, 5);
      });

      // Clear current round bets
      setCurrentRoundBets([]);
      setFakeRoundBets([]);
      fakeRoundIdRef.current = "";
    }

  // Store round result when game crashes
  if (gameData.status === "BLAST") {
      const roundResult =
        gameData.multiplier || gameData.crashPoint || 1.0;
      setPreviousRoundResult(roundResult);
      setCurrentRoundBets((prev) =>
        prev.map((bet) => ({ ...bet, crashMultiplier: roundResult })),
      );
      setPreviousRoundBets(
        currentRoundBetsRef.current.map((bet) => ({
          ...bet,
          crashMultiplier: roundResult,
        })),
      );
    }

    // Handle new round
    if (roundId && String(roundId) !== currentRoundRef.current) {
      // If we had a previous round, move it to history
      if (currentRoundRef.current !== "" && combinedCurrentBets.length > 0) {
        setRoundHistory((prev) => {
          const newHistory = [...prev];
          newHistory.unshift({
            roundId: currentRoundRef.current,
            bets: [...combinedCurrentBets],
            result: previousRoundResultRef.current || 1.0,
          });

          return newHistory.slice(0, 5);
        });
      }

      // Update current round reference
      currentRoundRef.current = String(roundId);

      // Filter bets for current round
      const currentBets = bets.filter((bet: any) => {
        // Check if bet belongs to current round
        const betRound = bet.roundId || bet.round;
        return betRound === String(roundId);
      });

      setCurrentRoundBets(currentBets);
    } else if (roundId) {
      // Update existing bets in current round
      setCurrentRoundBets((prev) => {
        const updatedBets = [...prev];
        let hasChanges = false;

        bets.forEach((bet: any) => {
          // Check if bet belongs to current round
          const betRound = bet.roundId || bet.round;
          if (betRound !== currentRoundRef.current) return;

          const existingIndex = updatedBets.findIndex(
            (existingBet) => existingBet.betId === bet.betId,
          );

          if (existingIndex > -1) {
            // Check if bet has changed (especially cashout status)
            const existingBet = updatedBets[existingIndex];
            const isCashoutUpdate =
              (bet.nation === "CASHOUT" || bet.status === "CASHOUT") &&
              existingBet.nation !== "CASHOUT";

            if (isCashoutUpdate) {
              // Merge existing bet with new data
              updatedBets[existingIndex] = {
                ...existingBet,
                ...bet,
                // Ensure cashout fields are properly set
                nation: bet.nation || existingBet.nation,
                status: bet.status || existingBet.status,
                cashOut: bet.cashOut || existingBet.cashOut,
                oddsRate: bet.oddsRate || bet.RUNValue || existingBet.oddsRate,
              };
              hasChanges = true;
            }
          } else {
            // Add new bet
            updatedBets.push({
              ...bet,
              // Ensure bet has all required fields
              avatar: bet.avatar || PLACEHOLDER_AVATAR,
              cashOut: bet.cashOut || 0,
              oddsRate: bet.oddsRate || bet.RUNValue || 1.0,
              nation: bet.nation || "PLACED",
              status: bet.status || "PLACED",
            });
            hasChanges = true;
          }
        });

        return hasChanges ? updatedBets : prev;
      });
    }

    // Update previous round bets from history
    if (roundHistoryRef.current.length > 0) {
      setPreviousRoundBets(roundHistoryRef.current[0].bets || []);
    }

    // Store previous game status
    prevGameStatusRef.current = gameData.status || "";
  }, [
    bets,
    gameData.status,
    gameData.multiplier,
    gameData.crashPoint,
    mergeRoundBets,
    roundId,
  ]);

  // Filter only current round bets for display
  const displayCurrentRoundBets = useMemo(() => {
    const roundKey = roundId ? String(roundId) : currentRoundRef.current;
    const socketBets = bets.filter((bet: any) => {
      const betRound = bet.roundId || bet.round;
      return betRound === roundKey;
    });
    const fakeBets = fakeRoundBets.filter(
      (bet) => String(bet.roundId || bet.round) === roundKey,
    );
    return mergeRoundBets(socketBets, fakeBets);
  }, [bets, fakeRoundBets, mergeRoundBets, roundId]);

  // Calculate statistics for current round
  const currentWinnerCount = displayCurrentRoundBets.filter(
    (bet:any) => bet.nation === "CASHOUT" || bet.status === "CASHOUT",
  ).length;

  const currentTotalBets = displayCurrentRoundBets.length;

  const currentTotalWin = displayCurrentRoundBets
    .filter((bet:any) => bet.nation === "CASHOUT" || bet.status === "CASHOUT")
    .reduce((sum, bet) => {
      const cashoutValue =
        bet.cashOut ||
        (bet.stake && bet.oddsRate ? parseFloat(bet.stake) * bet.oddsRate : 0);
      return sum + cashoutValue;
    }, 0);

  const [top] = useState(() => getDummyTop());

  return (
    <div
      className="flex flex-col w-full h-full lg:max-w-[480px] mx-auto bg-[#1b1c1d] rounded-2xl shadow-lg p-0 mt-1"
      style={{ maxHeight: "100vh" }}
    >
      <div className="flex flex-col w-full px-2 pt-2 pb-2">
        <div
          className="flex items-center mb-1 bg-[#141516] rounded-full "
          style={{ fontSize: "12px" }}
        >
          <Tab
            label="All Bets"
            active={tab === "All Bets"}
            onClick={() => setTab("All Bets")}
            rounded
          />
          <Tab
            label="Previous"
            active={tab === "Previous"}
            onClick={() => setTab("Previous")}
            rounded
          />
          <Tab
            label="Top"
            active={tab === "Top"}
            onClick={() => setTab("Top")}
            rounded
          />
        </div>

        {tab === "All Bets" && (
          <div className="bg-[#141516] px-3 py-1 rounded-xl">
            <div className="flex items-center justify-between w-full mb-1">
              <div className="flex -space-x-2">
                {displayCurrentRoundBets.slice(0, 3).map((u, i) => (
                  <img
                    key={u.betId || i}
                    src={u.avatar}
                    alt="avatar"
                    width={24}
                    height={24}
                    className="rounded-full border border-[#28a90a] bg-[#232325]"
                    onError={(e) => (e.currentTarget.src = PLACEHOLDER_AVATAR)}
                    style={{ zIndex: 10 - i }}
                  />
                ))}
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[16px] text-[#bbbfc5] leading-none">
                  {currentTotalWin.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1 justify-between mb-1">
              <div className="text-[12px] text-[#bbbfc5]">
                {currentWinnerCount}/{currentTotalBets}{" "}
                <span className="text-[#7b7b7b]"> Bets</span>
              </div>
              <div className="text-[12px] font-normal leading-[100%] align-middle text-[#7b7b7b]">
                Total win {currencyConfig.code}
              </div>
            </div>
            <div className="flex items-center w-full mb-1">
              <div className="flex-1 h-2 rounded-full bg-[#232325] overflow-hidden flex items-center">
                <div
                  className="h-1 rounded-full"
                  style={{
                    width: `${currentTotalBets > 0 ? (currentWinnerCount / currentTotalBets) * 100 : 0}%`,
                    background: GREEN_PROGRESS,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {tab === "Previous" && (
          <>
            {roundHistory.length > 0 ? (
              <div className="w-full text-center py-2 bg-[#141516] rounded-xl">
                <div className="text-xs text-[#7b7b7b]">Round Result</div>
                <span
                  className="text-[28px] font-extrabold"
                  style={{
                    color: getHistoryItemColor(previousRoundResult.toFixed(2)),
                  }}
                >
                  {previousRoundResult.toFixed(2)}x
                </span>
              </div>
            ) : (
              <div className="w-full text-center py-2 bg-[#141516] rounded-xl">
                <div className="text-xs text-[#7b7b7b]">
                  No previous round data
                </div>
              </div>
            )}
            <div className="flex px-4 py-1 mt-2 text-[#7b7b7b] text-[10px] mb-1">
              <span className="w-20">Player</span>
              <span className="w-24 text-right">Bet {currencyConfig.code}</span>
              <span className="w-20 text-center">X</span>
              <span className="w-20 text-right">Win {currencyConfig.code}</span>
            </div>
            <div
              className="flex-1 flex flex-col gap-[2px] w-full text-[12px] overflow-auto px-2 pb-2"
              style={{ maxHeight: "calc(100vh - 200px)" }}
            >
              {previousRoundBets.map((item, i) => {
                const isWinner =
                  item.nation === "CASHOUT" || item.status === "CASHOUT";
                return (
                <BetBoardItem
                  key={item.betId || i}
                  username={item.userName}
                  isYou={item.userName === username}
                  avatar={item.avatar}
                  betAmount={item.stake}
                  isWinner={isWinner}
                  crashedAt={
                      isWinner
                        ? item.oddsRate || item.RUNValue
                        : item.crashMultiplier || previousRoundResult || undefined
                    }
                  cashout={
                      isWinner ? item.cashOut || item.winAmount : undefined
                    }
                />
                );
              })}
            </div>
          </>
        )}

        {tab === "Top" && (
          <div className="bg-[#141516] rounded-xl text-[12px]">
            <div className="flex ">
              <Tab
                label="X"
                active={topTab === "X"}
                onClick={() => setTopTab("X")}
                rounded={false}
              />
              <Tab
                label="Win"
                active={topTab === "Win"}
                onClick={() => setTopTab("Win")}
                rounded={false}
              />
              <Tab
                label="Rounds"
                active={topTab === "Rounds"}
                onClick={() => setTopTab("Rounds")}
                rounded={false}
              />
            </div>
            <div className="flex">
              <Tab
                label="Day"
                active={topTabFilter === "Day"}
                onClick={() => setTopTabFilter("Day")}
                rounded={false}
              />
              <Tab
                label="Month"
                active={topTabFilter === "Month"}
                onClick={() => setTopTabFilter("Month")}
                rounded={false}
              />
              <Tab
                label="Year"
                active={topTabFilter === "Year"}
                onClick={() => setTopTabFilter("Year")}
                rounded={false}
              />
            </div>
          </div>
        )}
      </div>

      {tab === "All Bets" && (
        <div className="flex px-4 py-1 text-[#7b7b7b] text-[10px]">
          <span className="w-20">Player</span>
          <span className="w-24 text-right">Bet {currencyConfig.code}</span>
          <span className="w-20 text-center">X</span>
          <span className="w-20 text-right">Win {currencyConfig.code}</span>
        </div>
      )}

      <div className="flex-1 flex flex-col gap-[2px] w-full text-[12px] overflow-auto px-2 pb-2">
        {tab === "All Bets" &&
          displayCurrentRoundBets.map((item:any, i) => {
            const isWinner =
              item.nation === "CASHOUT" || item.status === "CASHOUT";
            return (
              <BetBoardItem
                key={item.betId || i}
                username={item.userName}
                isYou={item.userName === username}
                avatar={item.avatar}
                betAmount={item.stake}
                isWinner={isWinner}
                highlightCashout
                crashedAt={
                  isWinner ? item.oddsRate || item.RUNValue : undefined
                }
                cashout={isWinner ? item.cashOut : undefined}
              />
            );
          })}

        {tab === "Top" && (
          <>
            {top.map((item, i) => (
              <div
                key={i}
                className="bg-[#101112] rounded-xl px-2 py-2"
                style={{ marginBottom: "0.2rem" }}
              >
                <div className="flex items-start">
                  <img
                    src={item.avatar}
                    alt="avatar"
                    width={32}
                    height={32}
                    className="rounded-full mr-3 border border-[#18191b]"
                    onError={(e) => (e.currentTarget.src = PLACEHOLDER_AVATAR)}
                  />
                  <div className="flex-1">
                    <div className="flex flex-col">
                      <span className="font-normal text-[#bbbfc5] text-[12px]">
                        {item.username}
                      </span>
                      <span className="text-xs text-[#7b7b7b]">
                        {item.date}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-2">
                    <div className="w-8 h-8 bg-[#2c2d30] rounded-full flex items-center justify-center">
                      <MessageCircle className="w-4 h-4 text-[#7b7b7b]" />
                    </div>
                    <div className="w-8 h-8 bg-[#2c2d30] rounded-full flex items-center justify-center">
                      <ShieldCheck className="w-4 h-4 text-[#7b7b7b]" />
                    </div>
                  </div>
                </div>

                <div
                  className="grid grid-cols-2 gap-4 mt-1 w-full"
                  style={{ rowGap: "0.25rem" }}
                >
                  <div className="flex justify-between items-center text-xs text-[#7b7b7b]">
                    <span>Bet {currencyConfig.code}</span>
                    <span className="text-[#bbbfc5] text-[12px]">
                      {item.bet}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-[#7b7b7b]">
                    <span>Result</span>
                    <span
                      className="font-semibold text-[12px]"
                      style={{ color: PURPLE_X }}
                    >
                      {item.result}x
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-[#7b7b7b]">
                    <span>Win {currencyConfig.code}</span>
                    <span className="text-[#bbbfc5] text-[12px]">
                      {item.win.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-[#7b7b7b]">
                    <span>Round max.</span>
                    <span
                      className="font-bold text-[12px]"
                      style={{ color: PURPLE_X }}
                    >
                      {item.roundMax}x
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="flex items-center justify-between px-4 py-2 text-xs text-[#7b7b7b] border-t border-[#232325]">
        <span>Provably Fair Game</span>
        <span>
          Powered by{" "}
          <span className="font-semibold text-[#bbbfc5]">Meemplay</span>
        </span>
      </div>
    </div>
  );
};

export default BetBoard;
